import React from 'react';
import { TrendingUp, TrendingDown, Award, Target, Clock, Phone, Star, Zap, MoreVertical, Shield, Building2, ClipboardCheck, AlertCircle, Lightbulb, DollarSign, CheckCircle2 } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  trendLabel?: string;
}
function MetricCard({
  title,
  value,
  subtitle,
  icon,
  trend,
  trendLabel
}: MetricCardProps) {
  const isPositive = trend !== undefined && trend >= 0;
  return <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-388">
      <div className="flex items-start justify-between mb-3" data-id="element-389">
        <div className="flex-1" data-id="element-390">
          <p className="text-xs text-gray-500 font-medium mb-1" data-id="element-391">{title}</p>
          <p className="text-3xl font-bold text-gray-900 leading-none" data-id="element-392">
            {value}
          </p>
          {subtitle && <p className="text-xs text-gray-400 mt-1" data-id="element-393">{subtitle}</p>}
        </div>

        <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-[14px] flex items-center justify-center flex-shrink-0 shadow-sm" data-id="element-394">
          {icon}
        </div>
      </div>

      {trend !== undefined && <div className={`flex items-center gap-1 text-xs font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-395">
          {isPositive ? <TrendingUp className="w-3.5 h-3.5" data-id="element-396" /> : <TrendingDown className="w-3.5 h-3.5" data-id="element-397" />}
          <span data-id="element-398">
            {Math.abs(trend)}% {trendLabel}
          </span>
        </div>}
    </div>;
}
interface AchievementItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  points: number;
  date: string;
  color: string;
}
function AchievementItem({
  icon,
  title,
  description,
  points,
  date,
  color
}: AchievementItemProps) {
  return <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 flex items-center gap-4 shadow-sm border border-gray-100" data-id="element-399">
      <div className={`w-12 h-12 rounded-[16px] flex items-center justify-center flex-shrink-0 ${color}`} data-id="element-400">
        {icon}
      </div>

      <div className="flex-1 min-w-0" data-id="element-401">
        <h4 className="font-semibold text-gray-900 text-sm" data-id="element-402">{title}</h4>
        <p className="text-xs text-gray-500 truncate" data-id="element-403">{description}</p>
        <p className="text-xs text-gray-400 mt-1" data-id="element-404">{date}</p>
      </div>

      <div className="text-right flex-shrink-0" data-id="element-405">
        <p className="text-lg font-bold text-green-600" data-id="element-406">+{points}</p>
        <p className="text-xs text-gray-500" data-id="element-407">points</p>
      </div>
    </div>;
}
export function RepsScorePage() {
  const {
    currentUser
  } = useUser();
  // Format total points with 1 decimal place
  const totalPoints = currentUser?.averageScore.toFixed(1) || '0.0';
  const [integerPart, decimalPart] = totalPoints.split('.');
  return <div className="w-full px-6 pb-6 pt-4" data-id="element-408">
      <div className="flex items-center justify-between mb-6" data-id="element-409">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-410">Rep's Score</h2>
        <button className="text-sm font-medium text-blue-600 hover:text-blue-700" data-id="element-411">
          View Report
        </button>
      </div>

      {/* Hero Stats Card */}
      <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-6 shadow-sm border border-gray-100 mb-4" data-id="element-412">
        <div className="flex items-center justify-center mb-2" data-id="element-413">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-[18px] flex items-center justify-center shadow-lg shadow-blue-200/50" data-id="element-414">
            <Star className="w-7 h-7 text-white" data-id="element-415" />
          </div>
        </div>

        <div className="text-center" data-id="element-416">
          <p className="text-sm text-gray-500 font-medium mb-2" data-id="element-417">Total Points</p>
          <div className="flex items-baseline justify-center gap-1" data-id="element-418">
            <span className="text-5xl font-bold text-gray-900 tracking-tight" data-id="element-419">
              {integerPart}
            </span>
            <span className="text-3xl font-bold text-gray-400" data-id="element-420">
              .{decimalPart}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-421">
        <MetricCard title="Total Calls" value={currentUser?.totalCalls || 0} subtitle="all time" icon={<Phone className="w-5 h-5 text-gray-600" data-id="element-423" />} data-id="element-422" />

        <MetricCard title="Talk Time" value="0min" subtitle="all time" icon={<Clock className="w-5 h-5 text-gray-600" data-id="element-425" />} data-id="element-424" />
      </div>

      {/* Pitch Progress - Call Step Success Tiles */}
      <div className="mb-6" data-id="element-426">
        <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-427">
          Pitch Progress
        </h3>
        <div className="grid grid-cols-2 gap-3" data-id="element-428">
          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-429">
            <div className="flex items-center gap-3 mb-2" data-id="element-430">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-431">
                <Shield className="w-5 h-5 text-white" data-id="element-432" />
              </div>
              <div className="flex-1" data-id="element-433">
                <p className="text-xs text-gray-500 font-medium" data-id="element-434">Gate</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-435">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-436">
            <div className="flex items-center gap-3 mb-2" data-id="element-437">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-438">
                <Building2 className="w-5 h-5 text-white" data-id="element-439" />
              </div>
              <div className="flex-1" data-id="element-440">
                <p className="text-xs text-gray-500 font-medium" data-id="element-441">Open</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-442">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-443">
            <div className="flex items-center gap-3 mb-2" data-id="element-444">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-445">
                <ClipboardCheck className="w-5 h-5 text-white" data-id="element-446" />
              </div>
              <div className="flex-1" data-id="element-447">
                <p className="text-xs text-gray-500 font-medium" data-id="element-448">Eval</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-449">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-450">
            <div className="flex items-center gap-3 mb-2" data-id="element-451">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-452">
                <AlertCircle className="w-5 h-5 text-white" data-id="element-453" />
              </div>
              <div className="flex-1" data-id="element-454">
                <p className="text-xs text-gray-500 font-medium" data-id="element-455">Pain</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-456">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-457">
            <div className="flex items-center gap-3 mb-2" data-id="element-458">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-459">
                <Lightbulb className="w-5 h-5 text-white" data-id="element-460" />
              </div>
              <div className="flex-1" data-id="element-461">
                <p className="text-xs text-gray-500 font-medium" data-id="element-462">Solution</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-463">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100" data-id="element-464">
            <div className="flex items-center gap-3 mb-2" data-id="element-465">
              <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center" data-id="element-466">
                <DollarSign className="w-5 h-5 text-white" data-id="element-467" />
              </div>
              <div className="flex-1" data-id="element-468">
                <p className="text-xs text-gray-500 font-medium" data-id="element-469">Value</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-470">0</p>
              </div>
            </div>
          </div>

          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-gray-100 col-span-2" data-id="element-471">
            <div className="flex items-center gap-3 mb-2" data-id="element-472">
              <div className="w-10 h-10 bg-gradient-to-br from-lime-400 to-lime-500 rounded-full flex items-center justify-center" data-id="element-473">
                <CheckCircle2 className="w-5 h-5 text-white" data-id="element-474" />
              </div>
              <div className="flex-1" data-id="element-475">
                <p className="text-xs text-gray-500 font-medium" data-id="element-476">Close</p>
                <p className="text-2xl font-bold text-gray-900" data-id="element-477">0</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Achievements Section */}
      <div className="mb-4" data-id="element-478">
        <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-479">
          Recent Achievements
        </h3>
      </div>

      {currentUser?.callHistory && currentUser.callHistory.length > 0 ? <div className="space-y-3" data-id="element-480">
          {currentUser.callHistory.slice(0, 6).map((call, index) => <AchievementItem key={call.id} icon={<Phone className="w-6 h-6 text-blue-600" data-id="element-482" />} title={`Call Completed`} description={`${call.duration} call - ${call.score.toFixed(2)} points`} points={Math.round(call.score * 100)} date={new Date(call.date).toLocaleDateString()} color="bg-blue-100" data-id="element-481" />)}
        </div> : <div className="text-center py-12 bg-white/60 backdrop-blur-sm rounded-2xl border border-gray-100" data-id="element-483">
          <p className="text-gray-500" data-id="element-484">
            No achievements yet. Start making calls to earn achievements!
          </p>
        </div>}
    </div>;
}